import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ScrollText } from "lucide-react";
import {
  LEGAL_DOC_LABELS,
  LEGAL_DOC_SLUGS,
  LEGAL_ROUTES,
  legalDocListResponseSchema,
  legalDocResponseSchema,
  type LegalDoc,
  type LegalDocSlug,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, Textarea } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import { cn } from "@/shared/lib/cn";
import { AdminGuard } from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";

const NO_PREFIX = { baseUrl: "" } as const;

async function listDocs(): Promise<LegalDoc[]> {
  const data = await authFetch(LEGAL_ROUTES.adminList, legalDocListResponseSchema, NO_PREFIX);
  return data.items;
}

async function saveDoc(slug: LegalDocSlug, title: string, content: string): Promise<LegalDoc> {
  const data = await authFetch(LEGAL_ROUTES.adminOne(slug), legalDocResponseSchema, {
    ...NO_PREFIX,
    method: "PUT",
    body: { title, content },
  });
  return data.doc;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Documentos legales — réplica de `admin/legal-docs` (Anahí, 27-ago):
 * los cuatro que promete el pie del sitio, editables. Lo guardado es lo
 * que `/legal` pinta; un documento sin guardar usa el texto base del
 * sitio hasta que exista.
 *
 * La misma card de pestañas integradas del Plan de comisiones: una
 * pestaña por documento.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminLegalDocsPage() {
  return (
    <AdminGuard>
      <LegalScreen />
    </AdminGuard>
  );
}

function LegalScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const docs = useQuery({ queryKey: ["admin", "legal-docs"], queryFn: listDocs });

  const [slug, setSlug] = useState<LegalDocSlug>("privacidad");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stored = docs.data?.find((doc) => doc.slug === slug);

  // Al cambiar de pestaña, el borrador ES lo guardado (o el arranque).
  useEffect(() => {
    setTitle(stored?.title ?? LEGAL_DOC_LABELS[slug]);
    setContent(stored?.content ?? "");
    setSaved(false);
    setError(null);
  }, [slug, stored]);

  const guardar = useMutation({
    mutationFn: () => saveDoc(slug, title, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "legal-docs"] });
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!content.trim()) {
      setError(t("El documento no puede quedar vacío"));
      return;
    }
    guardar.mutate();
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Documentos legales")}
        description={t("Lo que promete el pie del sitio. Lo guardado es lo que /legal enseña.")}
      />

      {docs.isPending ? (
        <SkeletonCard />
      ) : (
        <form onSubmit={enviar}>
          <div className="overflow-hidden rounded-3xl border border-ds-border bg-white shadow-ds-float">
            <div
              role="tablist"
              aria-label={t("Documentos")}
              className="flex flex-wrap border-b border-ds-border bg-ds-cream-2/50"
            >
              {LEGAL_DOC_SLUGS.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={slug === item}
                  onClick={() => setSlug(item)}
                  className={cn(
                    "relative inline-flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold transition",
                    slug === item
                      ? "bg-white text-ds-violet-pop after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ds-violet-pop"
                      : "text-ds-ink-3 hover:bg-white/60 hover:text-ds-ink",
                  )}
                >
                  <ScrollText className="h-4 w-4" />
                  {t(LEGAL_DOC_LABELS[item])}
                </button>
              ))}
            </div>

            <div className="grid gap-4 p-5">
              <Input
                label={t("Título")}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={120}
              />
              <Textarea
                label={t("Contenido")}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                rows={16}
                placeholder={t("El texto completo del documento, con sus párrafos.")}
                hint={
                  stored
                    ? t("Última edición: {{fecha}}", { fecha: formatOrderDate(stored.updatedAt) })
                    : t("Todavía sin guardar: /legal usa el texto base del sitio.")
                }
              />
            </div>
          </div>

          {error && (
            <Alert variant="error" className="mt-4" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}
          {saved && (
            <Alert variant="success" className="mt-4" onDismiss={() => setSaved(false)}>
              {t("Guardado. /legal ya enseña esta versión.")}
            </Alert>
          )}

          <div className="mt-4 flex justify-end">
            <Button type="submit" loading={guardar.isPending}>
              {t("Guardar cambios")}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
